import { useState } from 'react';

import { Button } from '@components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@components/ui/dropdown-menu';
import { Skeleton } from '@components/ui/skeleton';
import { ENoteTypeFile } from '@enums/NoteTypeFile';
import { type Note } from '@models/Note';
import { type IDeleteNoteInputData } from '@services/notes/dtos/deleteNote/InputData';
import {
  EllipsisVertical,
  FilePen,
  FileText,
  FolderInput,
  LoaderCircle,
  Pill,
  Trash2,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router';

import DeleteAlertDialog from '../../DeleteAlertDialog';
import { type IOpenMoveFolderInputData } from '../../MoveDialog';

interface IProps extends Note {
  isLoading: boolean;
  onDelete: (data: IDeleteNoteInputData) => Promise<void>;
  onOpenMoveDialog: (inputData: IOpenMoveFolderInputData) => void;
}

function File({
  category,
  id,
  isLoading,
  onDelete,
  onOpenMoveDialog,
  title,
}: IProps) {
  const params = useParams();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="border-primary bg-background text-primary shadow-foreground/50 hover:shadow-foreground/60 flex items-center justify-center gap-2 rounded-lg border-2 text-base font-bold shadow-md transition-all duration-300 hover:scale-[1.02] lg:text-lg">
      <Link
        className="flex flex-1 py-3 pl-3 lg:py-5 lg:pl-5"
        to={`/anotacoes${params.id ? `/${params.id}/arquivos/${id}` : `/arquivos/${id}`}`}>
        <div className="flex flex-1 flex-row items-center gap-1">
          {category === ENoteTypeFile.ConditionOrDisease ? (
            <div>
              {isLoading ? (
                <LoaderCircle className="text-tertiary size-8 animate-spin" />
              ) : (
                <FileText className="size-9" strokeWidth={1.5} />
              )}
            </div>
          ) : (
            <div>
              {isLoading ? (
                <LoaderCircle className="text-tertiary size-8 animate-spin" />
              ) : (
                <Pill className="size-9" strokeWidth={1.5} />
              )}
            </div>
          )}
          {isLoading ? (
            <Skeleton className={`h-4 w-[180px]`} />
          ) : (
            <p className="text-wrap">{title}</p>
          )}
        </div>
      </Link>
      <DropdownMenu onOpenChange={setMenuIsOpen} open={menuIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button className="cursor-pointer" size="icon" variant="ghost">
            <EllipsisVertical className="text-primary" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuGroup>
            <DropdownMenuItem
              className="focus:bg-tertiary [&>svg]:size-6"
              onSelect={() =>
                navigate(
                  `/anotacoes${params.id ? `/${params.id}/arquivos/${id}/editar` : `/arquivos/${id}/editar`}`,
                )
              }>
              <FilePen className="size-6" strokeWidth={1.5} />
              Editar anotação
            </DropdownMenuItem>

            <DropdownMenuItem
              className="focus:bg-tertiary"
              onClick={(e) => {
                e.stopPropagation();
                setMenuIsOpen(false);
                onOpenMoveDialog({
                  category,
                  title,
                  id,
                  folderOriginId: params.id ? params.id : null,
                });
              }}
              onSelect={(e) => e.preventDefault()}>
              <FolderInput className="size-6" strokeWidth={1.5} />
              Mover pasta
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DeleteAlertDialog id={id} onDeleteNote={onDelete}>
              <DropdownMenuItem
                className="text-destructive focus:bg-destructive focus:text-background"
                onClick={(e) => e.stopPropagation()}
                onSelect={(e) => e.preventDefault()}>
                <Trash2 className="text-destructive size-6" strokeWidth={1.5} />
                Excluir
              </DropdownMenuItem>
            </DeleteAlertDialog>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default File;
